import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PurchaseRequestCardData } from '../../../interfaces/pruchase-request-card-data';
import { PurchaseRequester } from '../../../interfaces/purchase-requester';
import { PurchaseRequestStatus, UiCapability } from '../../../enums';
import { HasCapabilityDirective } from '../../../directives/has-capability.directive';
import { AbstractPurchasingService } from '../../../services/abstract-purchasing.service';
import { CommonModule } from '@angular/common';
import { AionzDialog, AionzTooltip, PipesModule, TooltipItem, UserAvatarComponent } from '@aionzdev/design-system';
import { MatBadgeModule } from '@angular/material/badge';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { formatDate } from '@angular/common';
import { KanbanChipBadgeComponent } from '../kanban-chip-badge/kanban-chip-badge.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { BranchEntry, BranchesSummary } from '../../../interfaces/branches-summary';
import { BranchesTooltipComponent } from '../branches-tooltip/branches-tooltip.component';
import { SupplierResponsesSummary } from '../../../interfaces/supplier-responses-summary';
import { SupplierResponsesTooltipComponent } from '../supplier-responses-tooltip/supplier-responses-tooltip.component';
import { Router, RouterLink } from '@angular/router';
import { TimePerStageTooltipComponent } from '../time-per-stage-tooltip/time-per-stage-tooltip.component';
import { OrderHistoryDialogComponent } from '../../components/order-history-dialog/order-history-dialog.component';
import { PurchaseRequestTimePerStageService } from '../../../services/purchase-request-time-per-stage.service';
import { PurchaseRequestOrderHistoryService } from '../../../services/purchase-request-order-history.service';

@Component({
  selector: 'aionz-purchase-request-card',
  imports: [
    CommonModule,
    MatBadgeModule,
    MatCardModule,
    MatDividerModule,
    MatIconModule,
    AionzTooltip,
    KanbanChipBadgeComponent,
    UserAvatarComponent,
    PipesModule,
    MatMenuModule,
    MatButtonModule,
    HasCapabilityDirective,
    RouterLink
  ],
  templateUrl: './purchase-request-card.component.html',
  styleUrl: './purchase-request-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchaseRequestCardComponent {
  protected readonly UiCapability = UiCapability;
  private readonly timePerStageService = inject(PurchaseRequestTimePerStageService);
  private readonly orderHistoryService = inject(PurchaseRequestOrderHistoryService);
  private readonly dialog = inject(AionzDialog);
  private readonly purchasingService = inject(AbstractPurchasingService);
  private readonly capabilities = toSignal(this.purchasingService.capabilities$, {
    initialValue: [] as UiCapability[]
  });

  public data = input.required<PurchaseRequestCardData>();
  public showDescription = input<boolean>(false);
  public verticalLayout = input<boolean>(false);

  public cardClicked = output<PurchaseRequestCardData>();
  public changeBuyer = output<PurchaseRequestCardData>();
  public cancelPurchaseRequest = output<PurchaseRequestCardData>();
  public openAuditReport = output<PurchaseRequestCardData>();

  protected readonly timePerStageComponent = TimePerStageTooltipComponent;
  protected readonly supplierResponsesTooltipComponent = SupplierResponsesTooltipComponent;
  protected readonly branchesTooltipComponent = BranchesTooltipComponent;

  private readonly router = inject(Router);

  /** Card com link navega pelo próprio `<a>`; só os cards de dialog emitem o clique. */
  public onCardClick() {
    if (this.data().link) return;
    this.cardClicked.emit(this.data());
  }

  /** Espaço abre o card só quando ele não é link (o `<a>` já trata Enter nativamente). */
  protected onCardSpace(event: Event): void {
    if (this.data().link) return;
    event.preventDefault();
    this.onCardClick();
  }

  /** A unificação fica no detalhe da solicitação aceita (aba Itens, a padrão). */
  protected get canUnify(): boolean {
    return !!this.data().link?.includes('../solicitacao-aceita');
  }

  protected get duplicatedTooltip(): string {
    const base = 'O Cotai encontrou outra solicitação com os mesmos produtos.';
    return this.canUnify ? `${base} Clique para ver e unificar.` : base;
  }

  protected get hasLink(): boolean {
    return !!this.data().link?.length;
  }

  /** Mesmo `state.returnUrl` que a navegação por código passava. */
  protected get linkState(): { returnUrl: string } {
    return { returnUrl: this.router.url };
  }

  protected getRequesterTooltipItem(requester: PurchaseRequester): TooltipItem[] {
    return [{
      id: requester.id,
      text: requester.name
    }];
  }

  protected getBuyerTooltipItem(): TooltipItem[] {
    return [{
      id: this.data().buyer?.responsibleName || 'buyer',
      text: this.data().buyer?.responsibleName || '--'
    }];
  }

  protected getRequestersTooltipItems() {
    return this.data().requesters.map(requester => ({
      id: requester.id,
      text: requester.name
    }));
  }

  /** Dados do tooltip do chip de fornecedores: quem respondeu e quem ainda não. */
  protected get supplierResponsesTooltipData(): SupplierResponsesSummary {
    const suppliers = this.data().suppliers ?? [];
    const pendingKeys = new Set((this.data().pendingSuppliers ?? []).map(s => s.id || s.email));
    const inQuotation = this.isInQuotation;
    const total = suppliers.length;
    const pending = inQuotation ? (this.data().pendingSuppliers?.length ?? total) : 0;

    return {
      inQuotation,
      total,
      responded: Math.max(total - pending, 0),
      hasPendingSuggestedSuppliers: this.hasPendingSuggestedSuppliers,
      suppliers: suppliers.map(supplier => ({
        id: supplier.id || `${this.data().id}-supplier-${supplier.email}`,
        name: supplier.name,
        email: supplier.email,
        responded: inQuotation && !pendingKeys.has(supplier.id || supplier.email)
      }))
    };
  }

  protected get requestedAtLabel(): string {
    return formatDate(this.data().requestedAt, 'dd/MM/yy', 'pt-BR');
  }

  protected get remainingDays(): number | null {
    const days = this.data().remainingDays;
    return typeof days === 'number' ? days : null;
  }

  protected get expectedDeliveryDateLabel(): string | null {
    const days = this.remainingDays;
    if (days === null) return null;

    const deliveryDate = new Date();
    deliveryDate.setHours(0, 0, 0, 0);
    deliveryDate.setDate(deliveryDate.getDate() + days);
    return formatDate(deliveryDate, 'dd/MM/yyyy', 'pt-BR');
  }

  protected get timePerStageItems() {
    return this.timePerStageService.getItems(this.data().timePerStage);
  }

  protected get totalTime(): number | null {
    return this.timePerStageService.getTotalTime(this.data().timePerStage);
  }

  protected get timePerStageTooltipData() {
    return {
      ...this.timePerStageService.getTooltipData(this.data().timePerStage, this.data().status[0]),
      footnote: this.deadlineFootnote
    };
  }

  protected get orderHistoryTooltipData() {
    return this.orderHistoryService.getTooltipData(this.data().id, this.data().orders);
  }

  protected get erpCodesLabel(): string {
    return (this.data().orders ?? []).map(o => o.orderCode).join(', ');
  }


  protected get showOrderHistoryChip(): boolean {
    return this.orderHistoryService.shouldShowChip(this.data().status, this.data().orders);
  }

  /** Prazo iminente (encerra hoje ou em até 2 dias): mostrado em âmbar, como atenção. */
  protected get isDeadlineWarning(): boolean {
    if (this.data().isDeadlineWarning) {
      return true;
    }

    const days = this.remainingDays;
    if (days === null) return false;
    return days >= 0 && days <= 2;
  }

  /** Prazo já passou: mostrado em vermelho, para não se confundir com o prazo folgado. */
  protected get isDeadlineOver(): boolean {
    const days = this.remainingDays;
    return days !== null && days < 0;
  }

  /** Rótulo do rodapé; a data exata fica no tooltip. */
  protected get deadlineLabel(): string {
    const days = this.remainingDays;
    if (days === null) return '--';
    if (days < 0) {
      const ago = Math.abs(days);
      return `Prazo encerrado há ${ago} ${ago === 1 ? 'dia' : 'dias'}`;
    }
    if (days === 0) return 'Prazo encerra hoje';
    return `Prazo em ${days} ${days === 1 ? 'dia' : 'dias'}`;
  }

  /** Linha final do tooltip de tempo por etapa, com o prazo por extenso. */
  protected get deadlineFootnote(): string | undefined {
    const days = this.remainingDays;
    const date = this.expectedDeliveryDateLabel;
    if (days === null || !date) return undefined;
    if (days < 0) {
      const ago = Math.abs(days);
      return `Prazo encerrado há ${ago} ${ago === 1 ? 'dia' : 'dias'} (${date})`;
    }
    if (days === 0) return `Prazo encerra hoje (${date})`;
    return `Prazo encerra em ${days} ${days === 1 ? 'dia' : 'dias'} (${date})`;
  }

  protected get showRemainingTime(): boolean {
    return !this.data().status.includes(PurchaseRequestStatus.QUOTED);
  }

  private get isAwaitingApproval(): boolean {
    return this.data().status.includes(PurchaseRequestStatus.PENDING);
  }

  private get isInQuotation(): boolean {
    return this.data().status.includes(PurchaseRequestStatus.IN_QUOTATION);
  }

  protected get showTopSuppliersCount(): boolean {
    if (this.isAwaitingApproval) return false;
    return (this.data().suppliers?.length ?? 0) > 0;
  }

  /**
   * Chip de fornecedores do cabeçalho. Fora da cotação mostra só a quantidade;
   * em cotação mostra "respondidos/total" e troca o ícone para a marca verde
   * quando todos responderam. Substitui o chip que ficava no rodapé.
   */
  protected get supplierChip(): { label: string; icon: string; color: string; complete: boolean; ariaLabel: string } {
    const total = this.data().suppliers?.length ?? 0;
    const neutral = 'var(--color-gray-7)';

    if (!this.isInQuotation) {
      return {
        label: `${total}`,
        icon: 'storefront',
        color: neutral,
        complete: false,
        ariaLabel: `${total} ${total === 1 ? 'fornecedor' : 'fornecedores'}`
      };
    }

    const pending = this.data().pendingSuppliers?.length ?? total;
    const responded = Math.max(total - pending, 0);
    const label = `${responded}/${total}`;

    // Verde só quando todos responderam; o resto fica neutro para não competir com o estado completo.
    if (total > 0 && responded >= total) {
      return {
        label,
        icon: 'check_circle',
        color: 'var(--color-success-darker, #076c13)',
        complete: true,
        ariaLabel: `Todos os ${total} fornecedores responderam`
      };
    }

    return {
      label,
      icon: 'storefront',
      color: neutral,
      complete: false,
      ariaLabel:
        responded > 0
          ? `${responded} de ${total} fornecedores responderam`
          : `Nenhum dos ${total} fornecedores respondeu`
    };
  }

  protected get hasPendingSuggestedSuppliers(): boolean {
    return Boolean(this.data().hasPendingSuggestedSuppliers);
  }

  /** Filiais distintas do pedido, com nome e endereço quando a API os traz. */
  protected get branchesTooltipData(): BranchesSummary {
    const seen = new Map<string, BranchEntry>();
    for (const item of this.data().file ?? []) {
      if (!item.branchId || seen.has(item.branchId)) continue;
      seen.set(item.branchId, {
        id: item.branchId,
        name: item.branchName || `Endereço #${item.branchId}`,
        street: item.branchStreet,
        city: item.branchCity
      });
    }
    return { branches: Array.from(seen.values()) };
  }

  protected get branchesCount(): number {
    const branchIds = (this.data().file ?? [])
      .map(item => item.branchId)
      .filter(Boolean);

    return new Set(branchIds).size;
  }

  protected get showBranchesCount(): boolean {
    return this.branchesCount > 0;
  }

  /**
   * Só o número: "filial 01" era lido como "01 filial", e nem toda unidade é filial (pode ser outra
   * empresa do grupo). O nome e o endereço de cada uma ficam no tooltip.
   */
  protected get branchesCountLabel(): string {
    return `${this.branchesCount}`;
  }

  protected get branchesAriaLabel(): string {
    return `${this.branchesCount} ${this.branchesCount === 1 ? 'local de entrega' : 'locais de entrega'}`;
  }

  protected get showBuyerSection(): boolean {
    return Boolean(this.data().buyer?.responsibleName || this.data().buyer?.avatarUrl);
  }

  protected get buyerResponsibleName(): string {
    return this.data().buyer?.responsibleName || '--';
  }

    protected get canChangeBuyer(): boolean {
    const statuses = this.data().status;
    const isPending = statuses.includes(PurchaseRequestStatus.PENDING);
    const isCompleted = statuses.includes(PurchaseRequestStatus.QUOTED);

    return !isPending && !isCompleted;
  }

  protected get canCancelPurchaseRequest(): boolean {
    return !this.data().status.includes(PurchaseRequestStatus.QUOTED);
  }

  protected get canOpenAuditReport(): boolean {
    return Boolean(this.data().urlRelatorioAuditoria);
  }

  private hasCapability(capability: UiCapability): boolean {
    return this.capabilities().includes(capability);
  }

  protected get hasOptions(): boolean {
    const canShowChangeBuyer = this.canChangeBuyer && this.hasCapability(UiCapability.PURCHASING_CHANGE_BUYER);
    const canShowCancelPurchaseRequest =
      this.canCancelPurchaseRequest && this.hasCapability(UiCapability.PURCHASING_CANCEL_PURCHASE_REQUEST);

    return canShowChangeBuyer || canShowCancelPurchaseRequest || this.canOpenAuditReport;
  }


  protected onChangeBuyer(): void {
    this.changeBuyer.emit(this.data());
  }

  protected onCancelPurchaseRequest(): void {
    this.cancelPurchaseRequest.emit(this.data());
  }

  protected onOpenAuditReport(): void {
    this.openAuditReport.emit(this.data());
  }

  protected openOrderHistoryDialog(event: Event): void {
    event.stopPropagation();
    this.dialog.open(OrderHistoryDialogComponent, {
      data: this.orderHistoryTooltipData,
    });
  }
}
